import {useEffect, useState} from "react";
import {useAuth} from "@/context/use-auth";
import {
    OrganizationDetails,
    CreateOrganizationDetailsDto,
    UpdateOrganizationDetailsDto,
    getOrganizationDetails,
    createOrganizationDetails,
    updateOrganizationDetails
} from "@/services/organization-details-services";
import {Button} from "@/components/tailwind/button";
import {Input} from "@/components/tailwind/input";
import {Field, Label} from "@/components/tailwind/fieldset";
import {Text} from "@/components/tailwind/text";
import {Heading} from "@/components/tailwind/heading";
import {useToast} from "@/hooks/use-toast";
import {useAdminTranslation, useCommonTranslation} from "@/i18n/hooks";
import {Building2, AlertCircle, Landmark} from "lucide-react";
import {Divider} from "@/components/tailwind/divider";
import {Card} from "@/components/ui/card";
import log from '@/lib/logger'

interface SectionOrganizationDetailsProps {
    isMissing?: boolean;
}

export function SectionOrganizationDetails({ isMissing = false }: SectionOrganizationDetailsProps) {
    const {t} = useAdminTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth();
    const {toast} = useToast();
    const [details, setDetails] = useState<OrganizationDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState<CreateOrganizationDetailsDto>({
        name: "",
        street: "",
        houseNumber: "",
        postalCode: "",
        city: "",
        phone: "",
        email: "",
        website: "",
        creditorIdentifier: "",
        creditorIban: "",
        creditorBic: ""
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        loadOrganizationDetails();
    }, [token]);

    async function loadOrganizationDetails() {
        try {
            setIsLoading(true);
            const data = await getOrganizationDetails(token);
            if (data) {
                setDetails(data as OrganizationDetails);
                setFormData({
                    name: data.name,
                    street: data.street || "",
                    houseNumber: data.houseNumber || "",
                    postalCode: data.postalCode || "",
                    city: data.city || "",
                    phone: data.phone || "",
                    email: data.email || "",
                    website: data.website || "",
                    creditorIdentifier: data.creditorIdentifier || "",
                    creditorIban: data.creditorIban || "",
                    creditorBic: data.creditorBic || ""
                });
            }
        } catch (error) {
            log.error("Failed to load organization details:", error);
            toast({
                title: t("adminSettings.organizationDetails.loadError.title"),
                description: t("adminSettings.organizationDetails.loadError.description"),
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = t("adminSettings.organizationDetails.validation.nameRequired");
        }

        // Validate complete address - all fields required
        if (!formData.street?.trim()) {
            newErrors.street = t("adminSettings.organizationDetails.validation.streetRequired");
        }
        if (!formData.houseNumber?.trim()) {
            newErrors.houseNumber = t("adminSettings.organizationDetails.validation.houseNumberRequired");
        }
        if (!formData.postalCode?.trim()) {
            newErrors.postalCode = t("adminSettings.organizationDetails.validation.postalCodeRequired");
        } else if (!/^\d{5}$/.test(formData.postalCode)) {
            newErrors.postalCode = t("adminSettings.organizationDetails.validation.invalidPostalCode");
        }
        if (!formData.city?.trim()) {
            newErrors.city = t("adminSettings.organizationDetails.validation.cityRequired");
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t("adminSettings.organizationDetails.validation.invalidEmail");
        }

        if (formData.phone && !/^[+]?[0-9\s\-/()]+$/.test(formData.phone)) {
            newErrors.phone = t("adminSettings.organizationDetails.validation.invalidPhone");
        }

        if (formData.website && !formData.website.match(/^(https?:\/\/)?.+\..+$/)) {
            newErrors.website = t("adminSettings.organizationDetails.validation.invalidWebsite");
        }

        if (formData.creditorIban && !/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/.test(formData.creditorIban.toUpperCase().replace(/\s/g, ""))) {
            newErrors.creditorIban = t("adminSettings.organizationDetails.validation.invalidIban");
        }

        if (formData.creditorBic && !/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(formData.creditorBic.toUpperCase())) {
            newErrors.creditorBic = t("adminSettings.organizationDetails.validation.invalidBic");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsSaving(true);

            const cleanedData = {
                ...formData,
                street: formData.street?.trim() || undefined,
                houseNumber: formData.houseNumber?.trim() || undefined,
                postalCode: formData.postalCode?.trim() || undefined,
                city: formData.city?.trim() || undefined,
                phone: formData.phone?.trim() || undefined,
                email: formData.email?.trim() || undefined,
                website: formData.website?.trim() || undefined,
                creditorIdentifier: formData.creditorIdentifier?.trim() || undefined,
                creditorIban: formData.creditorIban?.trim().toUpperCase().replace(/\s/g, "") || undefined,
                creditorBic: formData.creditorBic?.trim().toUpperCase() || undefined
            };


            let savedDetails: OrganizationDetails | void;
            if (details) {
                savedDetails = await updateOrganizationDetails(token, cleanedData as UpdateOrganizationDetailsDto);
            } else {
                savedDetails = await createOrganizationDetails(token, cleanedData as CreateOrganizationDetailsDto);
            }

            if (savedDetails) {
                setDetails(savedDetails as OrganizationDetails);
            }
            toast({
                title: t("adminSettings.organizationDetails.saveSuccess.title"),
                description: t("adminSettings.organizationDetails.saveSuccess.description")
            });
        } catch (error: any) {
            log.error("Failed to save organization details:", error);
            
            // Check if it's a ProblemDetail response
            if (error && error.fieldErrors) {
                const newErrors: Record<string, string> = {};
                
                Object.entries(error.fieldErrors).forEach(([field, message]) => {
                    newErrors[field] = message as string;
                });
                
                setErrors(newErrors);
                
                toast({
                    title: t("adminSettings.organizationDetails.validationError.title"),
                    description: t("adminSettings.organizationDetails.validationError.description"),
                    variant: "destructive"
                });
            } else if (error && error.detail) {
                toast({
                    title: t("adminSettings.organizationDetails.saveError.title"),
                    description: error.detail,
                    variant: "destructive"
                });
            } else {
                const errorMessage = error === "No token provided" 
                    ? t("adminSettings.organizationDetails.noTokenError") 
                    : t("adminSettings.organizationDetails.saveError.description");
                toast({
                    title: t("adminSettings.organizationDetails.saveError.title"),
                    description: errorMessage,
                    variant: "destructive"
                });
            }
        } finally {
            setIsSaving(false);
        }
    }

    function handleFieldChange(field: keyof CreateOrganizationDetailsDto, value: string) {
        setFormData(prev => ({...prev, [field]: value}));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = {...prev};
                delete newErrors[field];
                return newErrors;
            });
        }
    }

    if (isLoading) {
        return (
            <div className="py-8">
                <Text className="text-gray-500">{tCommon("loading")}</Text>
            </div>
        );
    }

    return (
        <Card className="p-6">
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <Building2 className="h-6 w-6 text-gray-500"/>
                    <Heading level={2} className="flex items-center gap-2">
                        {t("adminSettings.organizationDetails.title")}
                        {isMissing && (
                            <span 
                                title={t("adminSettings.missingConfiguration.tooltip")}
                                aria-label={t("adminSettings.missingConfiguration.ariaLabel")}
                            >
                                <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                            </span>
                        )}
                    </Heading>
                </div>


                <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                    <Field>
                        <Label htmlFor="org-name">
                            {t("adminSettings.organizationDetails.fields.name")} *
                        </Label>
                        <Input
                            id="org-name"
                            type="text"
                            value={formData.name}
                            onChange={e => handleFieldChange("name", e.target.value)}
                            invalid={!!errors.name}
                            data-testid="org-name"
                        />
                        {errors.name && (
                            <Text className="text-red-500 text-sm mt-1">{errors.name}</Text>
                        )}
                    </Field>

                    <Divider soft className="my-6"/>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field>
                            <Label htmlFor="org-street">
                                {t("adminSettings.organizationDetails.fields.street")} *
                            </Label>
                            <Input
                                id="org-street"
                                type="text"
                                value={formData.street}
                                onChange={e => handleFieldChange("street", e.target.value)}
                                invalid={!!errors.street}
                                data-testid="org-street"
                            />
                            {errors.street && (
                                <Text className="text-red-500 text-sm mt-1">{errors.street}</Text>
                            )}
                        </Field>

                        <Field>
                            <Label htmlFor="org-houseNumber">
                                {t("adminSettings.organizationDetails.fields.houseNumber")} *
                            </Label>
                            <Input
                                id="org-houseNumber"
                                type="text"
                                value={formData.houseNumber}
                                onChange={e => handleFieldChange("houseNumber", e.target.value)}
                                invalid={!!errors.houseNumber}
                                data-testid="org-houseNumber"
                            />
                            {errors.houseNumber && (
                                <Text className="text-red-500 text-sm mt-1">{errors.houseNumber}</Text>
                            )}
                        </Field>

                        <Field>
                            <Label htmlFor="org-postalCode">
                                {t("adminSettings.organizationDetails.fields.postalCode")} *
                            </Label>
                            <Input
                                id="org-postalCode"
                                type="text"
                                value={formData.postalCode}
                                onChange={e => handleFieldChange("postalCode", e.target.value)}
                                invalid={!!errors.postalCode}
                                maxLength={5}
                                data-testid="org-postalCode"
                            />
                            {errors.postalCode && (
                                <Text className="text-red-500 text-sm mt-1">{errors.postalCode}</Text>
                            )}
                        </Field>

                        <Field>
                            <Label htmlFor="org-city">
                                {t("adminSettings.organizationDetails.fields.city")} *
                            </Label>
                            <Input
                                id="org-city"
                                type="text"
                                value={formData.city}
                                onChange={e => handleFieldChange("city", e.target.value)}
                                invalid={!!errors.city}
                                data-testid="org-city"
                            />
                            {errors.city && (
                                <Text className="text-red-500 text-sm mt-1">{errors.city}</Text>
                            )}
                        </Field>
                    </div>

                    <Divider soft className="my-6"/>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field>
                            <Label htmlFor="org-phone">
                                {t("adminSettings.organizationDetails.fields.phone")}
                            </Label>
                            <Input
                                id="org-phone"
                                type="tel"
                                value={formData.phone}
                                onChange={e => handleFieldChange("phone", e.target.value)}
                                invalid={!!errors.phone}
                                data-testid="org-phone"
                            />
                            {errors.phone && (
                                <Text className="text-red-500 text-sm mt-1">{errors.phone}</Text>
                            )}
                        </Field>

                        <Field>
                            <Label htmlFor="org-email">
                                {t("adminSettings.organizationDetails.fields.email")}
                            </Label>
                            <Input
                                id="org-email"
                                type="email"
                                value={formData.email}
                                onChange={e => handleFieldChange("email", e.target.value)}
                                invalid={!!errors.email}
                                data-testid="org-email"
                            />
                            {errors.email && (
                                <Text className="text-red-500 text-sm mt-1">{errors.email}</Text>
                            )}
                        </Field>

                        <Field className="md:col-span-2">
                            <Label htmlFor="org-website">
                                {t("adminSettings.organizationDetails.fields.website")}
                            </Label>
                            <Input
                                id="org-website"
                                type="url"
                                value={formData.website}
                                onChange={e => handleFieldChange("website", e.target.value)}
                                invalid={!!errors.website}
                                placeholder="https://example.com"
                                data-testid="org-website"
                            />
                            {errors.website && (
                                <Text className="text-red-500 text-sm mt-1">{errors.website}</Text>
                            )}
                        </Field>
                    </div>

                    <Divider soft className="my-6"/>

                    <div className="flex items-center gap-2 mb-3">
                        <Landmark className="h-5 w-5 text-gray-500"/>
                        <Text className="font-medium">{t("adminSettings.organizationDetails.sepaSection.title")}</Text>
                    </div>
                    <Text className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {t("adminSettings.organizationDetails.sepaSection.description")}
                    </Text>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field className="md:col-span-2">
                            <Label htmlFor="org-creditorIdentifier">
                                {t("adminSettings.organizationDetails.fields.creditorIdentifier")}
                            </Label>
                            <Input
                                id="org-creditorIdentifier"
                                type="text"
                                value={formData.creditorIdentifier}
                                onChange={e => handleFieldChange("creditorIdentifier", e.target.value)}
                                invalid={!!errors.creditorIdentifier}
                                placeholder="DE98ZZZ09999999999"
                                maxLength={35}
                                data-testid="org-creditorIdentifier"
                            />
                            {errors.creditorIdentifier && (
                                <Text className="text-red-500 text-sm mt-1">{errors.creditorIdentifier}</Text>
                            )}
                        </Field>

                        <Field>
                            <Label htmlFor="org-creditorIban">
                                {t("adminSettings.organizationDetails.fields.creditorIban")}
                            </Label>
                            <Input
                                id="org-creditorIban"
                                type="text"
                                value={formData.creditorIban}
                                onChange={e => handleFieldChange("creditorIban", e.target.value)}
                                invalid={!!errors.creditorIban}
                                placeholder="DE89370400440532013000"
                                maxLength={34}
                                data-testid="org-creditorIban"
                            />
                            {errors.creditorIban && (
                                <Text className="text-red-500 text-sm mt-1">{errors.creditorIban}</Text>
                            )}
                        </Field>

                        <Field>
                            <Label htmlFor="org-creditorBic">
                                {t("adminSettings.organizationDetails.fields.creditorBic")}
                            </Label>
                            <Input
                                id="org-creditorBic"
                                type="text"
                                value={formData.creditorBic}
                                onChange={e => handleFieldChange("creditorBic", e.target.value)}
                                invalid={!!errors.creditorBic}
                                placeholder="COBADEFFXXX"
                                maxLength={11}
                                data-testid="org-creditorBic"
                            />
                            {errors.creditorBic && (
                                <Text className="text-red-500 text-sm mt-1">{errors.creditorBic}</Text>
                            )}
                        </Field>
                    </div>
                </div>

                <div className="flex items-center justify-end pt-4">
                    <Button
                        type="submit"
                        disabled={isSaving}
                        data-testid="save-org-details"
                    >
                        {isSaving ? tCommon("saving") : tCommon("save")}
                    </Button>
                </div>
                </form>
            </div>
        </Card>
    );
}